import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { toast } from 'react-toastify';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { debounce } from 'lodash';
import LoadingDots from './LoadingDots';
import Loader from './Loader';

const containerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 28.64231,
  lng: 77.22229,
};

const ListingStep4 = ({ formData, handleChange, prevStep, onRegisterSave }) => {
  const { listingId } = formData;
  const [listingData, setListingData] = useState([]);
  const [center, setCenter] = useState(defaultCenter);
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [placeId, setPlaceId] = useState();
  const [placeDetails, setPlaceDetails] = useState();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [district, setDistrict] = useState('');
  const [nearby, setNearby] = useState('');
  const [street, setStreet] = useState('');
  const [flatNo, setFlatNo] = useState('');
  const [question, setQuestion] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyC4xmFbX2XiP2dbZW5LUoSjw2qR-o2utmU",
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          setCenter({ lat: userLat, lng: userLng });
          setLatitude(userLat);
          setLongitude(userLng);
        },
        (error) => {
          console.error('Error retrieving user location:', error);
        }
      );
    }
  }, []);

  const fetchAddress = useCallback(
    debounce((lat, lng) => {
      setIsLoading(true);
      fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyC4xmFbX2XiP2dbZW5LUoSjw2qR-o2utmU`)
        .then((response) => response.json())
        .then((data) => {
          if (data.results && data.results[0]) {
            const addressComponents = data.results[0].address_components;
            let state_name = '';
            let country_name = '';
            let postalCode = '';
            let city = '';
            let district = '';

            addressComponents.forEach((component) => {
              if (component.types.includes('administrative_area_level_1')) {
                state_name = component.long_name;
              }
              if (component.types.includes('administrative_area_level_2')) {
                district = component.long_name;
              }
              if (component.types.includes('locality')) {
                city = component.long_name;
              }
              if (component.types.includes('country')) {
                country_name = component.long_name;
              }
              if (component.types.includes('postal_code')) {
                postalCode = component.long_name;
              }
            });
            setAddress(data.results[0].formatted_address);
            setDistrict(district);
            setCity(city);
            setState(state_name);
            setPincode(postalCode);
          } else {
            setAddress('Address not found');
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching the address:', error);
          setAddress('Error retrieving address');
          setIsLoading(false);
        });
    }, 500),
    []
  );

  const fetchPlaceSuggestions = useCallback(
    debounce(async (text) => {
      if (!text) return;
      const hostData = getItemFromLocalStorage('hostProfile');
      const country = 'in';

      let locData = new FormData();
      locData.append('token', hostData.host_access_token);
      locData.append('country', country);
      locData.append('text', text);

      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          url: `${params.baseURL}${params.endpoints.findPlaces}`,
          data: locData,
        });

        if (response.data.status === 'result') {
          const suggestions = response.data.places_list;
          setPlaceSuggestions(suggestions);
        } else if (response.data.status === 'error') {
          response.data.list.forEach((errorMsg) => {
            toast.error(errorMsg);
          });
        } else {
          toast.error('Failed to fetch data');
        }
      } catch (err) {
        console.log('An error occurred while saving the listing');
      }
    }, 500),
    []
  );

  const fetchPlaceDetails = async (placeId) => {
    const hostData = getItemFromLocalStorage('hostProfile');
    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('id', placeId);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.findPlaceById}`,
        data: formData,
      });

      if (response.data.status === 'found') {
        const details = response.data.property_location;
        setPlaceDetails(details);
        setAddress(details.state);
        setCenter({ lat: details.lat, lng: details.lng });
        setLatitude(details.lat);
        setLongitude(details.lng);
        setCountry(details.country_name);
        setState(details.state);
        setCity(details.city);
        setNearby(details.nearby);
        setPincode(details.pincode);
      } else if (response.data.status === 'error') {
        response.data.list.forEach((errorMsg) => {
          toast.error(errorMsg);
        });
      } else {
        toast.error('An error occurred while fetching data.');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleInputChange = (e) => {
    const inputText = e.target.value;
    if (inputText.length > 3) fetchPlaceSuggestions(inputText);
  };

  const handleSuggestionClick = (suggestion) => {
    setPlaceId(suggestion.id);
    fetchPlaceDetails(suggestion.id);
    setIsFormVisible(true);
    setPlaceSuggestions([]);
  };

  const prevCoords = useRef({ lat: null, lng: null });

  const handleMapIdle = () => {
    if (mapRef.current) {
      const mapCenter = mapRef.current.getCenter();
      const lat = mapCenter?.lat();
      const lng = mapCenter?.lng();

      if (
        Math.abs(lat - prevCoords.current.lat) > 0.001 ||
        Math.abs(lng - prevCoords.current.lng) > 0.001
      ) {
        prevCoords.current = { lat, lng };
        setLatitude(lat);
        setLongitude(lng);
        fetchAddress(lat, lng);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let newErrors = {
      flatNo: isFieldEmpty(flatNo),
      street: isFieldEmpty(street),
      district: isFieldEmpty(district),
      city: isFieldEmpty(city),
      state: isFieldEmpty(state),
      pincode: isFieldEmpty(pincode),
    };

    newErrors[name] = isFieldEmpty(value)

    setErrors(newErrors);
    switch (name) {
      case 'flatNo':
        setFlatNo(value);
        break;
      case 'street':
        setStreet(value);
        break;
      case 'nearby':
        setNearby(value);
        break;
      case 'district':
        setDistrict(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'pincode':
        setPincode(value);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const fetchListingData = async () => {
      const hostData = getItemFromLocalStorage('hostProfile');
      let formData = new FormData();
      formData.append('token', hostData.host_access_token);
      formData.append('listing_id', listingId);

      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          url: `${params.baseURL}${params.endpoints.getListingData}`,
          data: formData,
        });

        if (response.data.status === 'ok') {
          const listingData = response.data.updated_property_item;
          const { appr_list, resrv_list, offers, place_options_list } = listingData;
          setListingData(response.data.updated_property_item);
          setQuestion(response.data.updated_property_item.questions);
          setLoading(false);

          if (listingData.street_address) {
            setAddress(listingData.property_address);
            setCenter({ lat: listingData.listing_lat, lng: listingData.longitude });
            setLatitude(listingData.listing_lat);
            setLongitude(listingData.longitude);
          } else {
            if (latitude && longitude) {
              setCenter({ lat: latitude, lng: longitude });
              fetchAddress(latitude, longitude);
            }
          }

          setStreet(listingData.street_address)
          setState(listingData.state_ut);
          setCity(listingData.city_town);
          setNearby(listingData.nearby_landmark);
          setPincode(listingData.pincode);
          setFlatNo(listingData.flat_house_info);
          setDistrict(listingData.district);
        } else {
          toast.error('An error occurred while fetching data.');
          setLoading(false);
        }
      } catch (err) {
        console.log(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchListingData();
  }, [listingId, latitude, longitude, fetchAddress]);

  const isFieldEmpty = (value) => !value || value.trim() === '';


  const saveListing = async () => {

    const newErrors = {
      flatNo: isFieldEmpty(flatNo),
      street: isFieldEmpty(street),
      district: isFieldEmpty(district),
      city: isFieldEmpty(city),
      state: isFieldEmpty(state),
      pincode: isFieldEmpty(pincode),
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((val) => val);

    if (hasErrors) {
      return;
    }

    const hostData = getItemFromLocalStorage('hostProfile');
    const listing_id = listingId;
    const ts = Date.now();

    const updatedPropertyItem = {
      flat_house_info: flatNo,
      street_address: street,
      nearby_landmark: nearby,
      district: district,
      city_town: city,
      state_ut: state,
      pincode: pincode,
      listing_lat: latitude,
      listing_lng: longitude,
    };

    const mergedData = { ...listingData, ...updatedPropertyItem };

    let catData = new FormData();
    catData.append('token', hostData.host_access_token);
    catData.append('listing_id', listing_id);
    catData.append('data', JSON.stringify(mergedData));
    catData.append('ts', ts);

    setNextLoading(true);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        url: `${params.baseURL}${params.endpoints.updateListing}`,
        data: catData,
      });

      if (response.data.status === 'done') {
        return true;
      } else {
        toast.error('Failed to save data');
        return false;
      }
    } catch (err) {
      toast.error('An error occurred while saving the listing');
      return false;
    }
  };



  const handleEditAddress = () => {
    setIsFormVisible(true);
  }

  useEffect(() => {
    return () => {
      fetchAddress.cancel();
      fetchPlaceSuggestions.cancel();
    };
  }, [fetchAddress, fetchPlaceSuggestions]);

  const faqs = [
    {
      question: "What is Lorem Ipsum?",
      answer: "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book."
    },
    {
      question: "Where does it come from?",
      answer: "Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old."
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  if (loading) {
    return (
      <Loader />
    );
  }

  return (
    <div className="fade-in">
      <button id='host-listing-trigger-4' style={{ display: 'none' }} onClick={() => {
        if (onRegisterSave) {
          onRegisterSave(saveListing);
        }
      }}>Done</button>
      <div className='row'>
        {/*<div className='col-md-4 listing-img-box'>
            <div className='lsib'>
              <img src={'/assets/img/listing/listing_side_img.gif'} alt="Listing Image" className="listing-image" />
              {question && (
                <div className='lsqa-btn'>
                  <StarButton text="Guide" link={question.find((q) => q.id === 4)?.link || '#'} />
                </div>
              )}
            </div>
          </div>*/}

        <div className='col-md-7'>

          <div className='ws-cl-header mb-0'>
            <div>
              <h4 className='ws-title'>Enter property address</h4>
              <p className='ws-desc mb-1'>Enter your full address in the field showing your location.</p>
              <p className='ws-desc'>For updates, use the map’s search bar. Provide a complete and accurate address to help guests find your place easily.</p>
            </div>
          </div>

          <div className="st1-desc-box">
            {isLoading ? (
              <div className="selected-address">
                <p>Loading...</p>
              </div>
            ) : (
              address && (
                <div className="selected-address" onClick={handleEditAddress}>
                  <p>{address}</p>
                </div>
              )
            )}

            <div className='row'>
              <div className='col-md-6 ps-0'>
                <div className="map-container">
                  <div className="search-address-box">
                    <input
                      name="find-place"
                      className="form-control"
                      id="find-place"
                      placeholder="Enter your address"
                      onChange={handleInputChange}
                      autoComplete='off'
                    />

                    {placeSuggestions.length > 0 && (
                      <ul className="suggestions-list">
                        {placeSuggestions.map((suggestion, index) => (
                          <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                            <p className='loc-title'>{suggestion.title}</p>
                            <p className='loc-desc'>{suggestion.description}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div style={containerStyle} id="googleMap">
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={center}
                      zoom={10}
                      onLoad={(map) => (mapRef.current = map)}
                      onIdle={handleMapIdle}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '70%',
                        left: '50%',
                        transform: 'translate(-50%, -100%)',
                        pointerEvents: 'none',
                      }}
                    >
                      <img src={'/assets/img/icons/location_marker_2.svg'} style={{ width: '40px' }} alt="Map Marker" />
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-md-6 pe-0'>
                <div className="ld-row">
                  <div className='location-details-box'>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating position-relative">
                            <input
                              className={`form-control ${errors.flatNo ? 'input-error' : ''}`}
                              type="text"
                              id="flatNo"
                              name="flatNo"
                              value={flatNo}
                              onChange={handleFormChange}
                            />
                            <label htmlFor="flatNo">Flat No</label>

                            {errors.flatNo && (
                              <span className="error-icon">
                                <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                <span className="tooltip-text">This field is required</span>
                              </span>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating position-relative">
                            <input className={`form-control ${errors.street ? 'input-error' : ''}`} type="text" id="street" name="street" value={street} onChange={handleFormChange} />
                            <label htmlFor="street">Street address</label>

                            {errors.street && (
                              <span className="error-icon">
                                <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                <span className="tooltip-text">This field is required</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating">
                            <input className={'form-control'} type="text" id="nearby" name="nearby" value={nearby} onChange={handleFormChange} />
                            <label htmlFor="nearby">Nearby landmark</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating">
                            <input className={`form-control ${errors.district ? 'input-error' : ''}`} type="text" id="district" name="district" value={district} onChange={handleFormChange} />
                            <label htmlFor="nearby">District/Locality</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating">
                            <input className={`form-control ${errors.city ? 'input-error' : ''}`} type="text" id="city" name="city" value={city} onChange={handleFormChange} />
                            <label htmlFor="nearby">City/Town</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating">
                            <input className={`form-control ${errors.state ? 'input-error' : ''}`} type="text" id="state" name="state" value={state} onChange={handleFormChange} />
                            <label htmlFor="nearby">State</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-md-12 mb-3">
                      <div className='form-flex'>
                        <div className='form-icon'>
                          <img
                            src='/assets/img/dummy_amenity_icon.svg'
                            className='form-icon img-fluid'
                            alt=''
                          />
                        </div>
                        <div className='form-txt'>
                          <div className="form-floating position-relative">
                            <input className={`form-control ${errors.pincode ? 'input-error' : ''}`} type="text" id="pincode" name="pincode" value={pincode} onChange={handleFormChange} />
                            <label htmlFor="nearby">Pincode</label>
                            {errors.pincode && (
                              <span className="error-icon">
                                <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                <span className="tooltip-text">This field is required</span>
                              </span>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className='col-md-1'></div>

        <div className='col-md-4 p-0'>
          <div className='bfhsd'>
            <div className='blog-box'>
              <div class="blog">
                <div class="blog-image">
                  <img src={'https://app.wirestay.com/ws/uploads/blog/1736870810_pi.jpg'} className='img-fluid' alt="" />
                  <div class="date">May 08, 2025</div>
                </div>
                <div class="blog-content">
                  <h2>What is Lorem Ipsum?</h2>
                  <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Mauris vel metus vel est fermentum consectetur.</p>
                  <a href="#">Read More</a>
                </div>
              </div>
            </div>
            <div className="faq-sticky">
              {faqs.map((faq, index) => (
                <div key={index} className='faqs-list-section'>
                  <div
                    onClick={() => toggleAccordion(index)} className='faqs-qustn-box'
                    style={{
                      backgroundColor: activeIndex === index ? "#f7f7f7" : "#ffffff",
                    }}
                  >
                    {faq.question}
                    <i
                      className={`fas ${activeIndex === index ? "fa-chevron-up" : "fa-chevron-down"
                        }`}
                      style={{
                        transition: "transform 0.3s ease",
                      }}
                    ></i>
                  </div>
                  {activeIndex === index && (
                    <div className='faqs-ans-box'>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingStep4;
